/**
 * Application state types for SAE Studio (apps/web).
 *
 * The store is framework-free: React binds to it, tests drive it
 * directly, and the worker pipeline consumes the same shapes. All
 * survey data lives in these objects in the browser only (brief P1);
 * the single network-touching state is `license`.
 */

import type {
  Assessment,
  CollapseRule,
  FeasibilityPreview,
  IndicatorRule,
  QcCheckResult,
  RoleMap,
} from '@sae/automation';
import type { AnalysisResult, ProgressEvent } from '@sae/engine';
import type { DataTable, IoIssue, SourceFormat } from '@sae/io';

/** The four frozen steps (DESIGN_RECORD item 2; mockup step bar). */
export type StepId = 'upload' | 'configure' | 'results' | 'diagnostics';

export const STEP_ORDER: readonly StepId[] = [
  'upload',
  'configure',
  'results',
  'diagnostics',
];

export const STEP_LABELS: Record<StepId, { title: string; sub: string }> = {
  upload: { title: 'Upload & Profile', sub: 'File, Roles, and Coverage Check' },
  configure: {
    title: 'Configure Model',
    sub: 'Covariates, Transform, Estimator',
  },
  results: { title: 'Results', sub: 'EBLUP Table and Chart' },
  diagnostics: {
    title: 'Diagnostics & Export',
    sub: 'Checks, Interpretation, Files',
  },
};

/** Where the working data came from. 'demo' is exempt from the trial cap (S3). */
export type SourceKind = 'none' | 'demo' | 'own';

export interface SourceState {
  readonly kind: SourceKind;
  readonly filename: string | null;
  readonly format: SourceFormat | null;
  readonly table: DataTable | null;
  /** True when the table itself was not restored from storage (metadata only). */
  readonly tableMissing: boolean;
  readonly readIssues: readonly IoIssue[];
}

export interface CovariatesSourceState {
  readonly filename: string | null;
  readonly table: DataTable | null;
  /** Roles detected on the companion file (area id + covariate columns). */
  readonly roles: RoleMap | null;
}

/** Per-covariate user intent (brief 5.2 step 7: lock or exclude, else automatic). */
export type CovariateMode = 'auto' | 'locked' | 'excluded';

/** Disclosure preset choices shown in Step 2 (mockup preset row). */
export type DisclosurePresetId = 'standard' | 'strict' | 'off' | 'sae-standard';

/** Run configuration edited in Step 2 (brief Sections 9, 17; decisions S4-S6). */
export interface RunConfigState {
  /** Quick = analytical MSE (9.9); Full = parametric bootstrap MSE (9.12). */
  readonly mode: 'quick' | 'full';
  /** 'auto' resolves by outcome type and is declared in the output (S4). */
  readonly transform: 'auto' | 'arcsin' | 'linear';
  /** Benchmarking is on by default (D-4, superseding S5); one click turns it off. */
  readonly benchmarkingEnabled: boolean;
  readonly benchmarkingMethod: 'logit' | 'ratio';
  /**
   * Transform mean-bias correction on the arcsine inputs (D-3
   * part 2). The validated pairing is correction on with the
   * pooled deff basis; see docs/CORRECTION_VALIDATION.md.
   */
  readonly transformBiasCorrection: boolean;
  /** Design-effect smoothing basis for psi (brief 9.4). */
  readonly deffBasis: 'pooled' | 'statewide';
  readonly disclosurePresetId: DisclosurePresetId;
  /** Bootstrap replicates for Full mode (brief 9.12 minimum 200; shipped default 200 since re-review RB-3, 2026-10-17). */
  readonly bootstrapB: number;
  /** Run seed, recorded in the manifest (brief P7, 9.12). */
  readonly seed: string;
  readonly confidence: number;
}

/** Confirmed setup: the automation output the user has reviewed (Step 1-2). */
export interface SetupState {
  readonly assessment: Assessment;
  /** Working role map: detection output as confirmed/edited by the user. */
  readonly roles: RoleMap;
  /** Microdata: explicit indicator rules by source column (brief AUT-03). */
  readonly indicatorRules: Readonly<Record<string, IndicatorRule>>;
  /** Microdata: rule ids the user has switched on for this run. */
  readonly selectedIndicators: readonly string[];
  /** Area-level: the indicator id stamped on the run (one estimate column). */
  readonly areaIndicatorId: string;
  /** Area-level: declared scale of the estimate column (never guessed, Q21). */
  readonly estimateScale: 'proportion' | 'percent';
  /** Accepted subgroup collapse rules by variable (brief 5.2 step 6). */
  readonly collapses: Readonly<Record<string, CollapseRule>>;
  /** Subgroup variables switched on for this run. */
  readonly subgroupsSelected: readonly string[];
  readonly covariateModes: Readonly<Record<string, CovariateMode>>;
  /** Population counts by area id (benchmarking, QC-18), when supplied. */
  readonly populationCounts: Readonly<Record<string, number>> | null;
}

export type RunStatus = 'idle' | 'running' | 'done' | 'error';

export interface RunState {
  readonly status: RunStatus;
  readonly progress: readonly ProgressEvent[];
  readonly result: AnalysisResult | null;
  /** True when setup or configuration changed after the result was produced. */
  readonly stale: boolean;
  readonly error: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
}

export interface LicenseState {
  readonly status: 'trial' | 'licensed';
  readonly email: string | null;
  /** ISO timestamp the pass was issued (server-side verification, D3). */
  readonly verifiedAt: string | null;
  /** ISO timestamp the license itself expires (annual term, S2). */
  readonly expiresAt: string | null;
  readonly lastError: string | null;
}

export interface ProjectState {
  readonly version: 1;
  readonly projectName: string;
  readonly step: StepId;
  readonly furthestStep: StepId;
  readonly source: SourceState;
  readonly covariatesSource: CovariatesSourceState | null;
  readonly setup: SetupState | null;
  readonly config: RunConfigState;
  readonly qc: readonly QcCheckResult[] | null;
  readonly feasibility: FeasibilityPreview | null;
  readonly run: RunState;
  readonly license: LicenseState;
  /** Results view: the model cell shown in Step 3. */
  readonly selectedCellId: string | null;
  /** Autosave keeps the data file with the project (this browser only). */
  readonly keepDataWithProject: boolean;
  /** Bumped on every mutation; the autosave watermark. */
  readonly revision: number;
}

export const TRIAL_AREA_CAP = 10;
